import React, { useState, useEffect } from 'react';
import adminApi from '../utils/adminApi';
import { API_BASE_URL } from '../config';
import '../styles/AdminOverrides.css';

const AdminOverrides = () => {
  // User Balance Override
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newBalance, setNewBalance] = useState('');
  const [users, setUsers] = useState([]);

  // Stock Price Override
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState('');
  const [newStockPrice, setNewStockPrice] = useState('');

  // Reset Individual User
  const [resetUserId, setResetUserId] = useState('');

  // Award/Deduct Balance
  const [awardMode, setAwardMode] = useState('user'); // 'user' or 'team'
  const [awardUserSearch, setAwardUserSearch] = useState('');
  const [awardSelectedUsers, setAwardSelectedUsers] = useState([]);
  const [awardAmount, setAwardAmount] = useState('');
  const [awardReason, setAwardReason] = useState('');
  const [awardGame, setAwardGame] = useState('');
  const [awardTeam, setAwardTeam] = useState('');
  const [gameTeams, setGameTeams] = useState([]);

  // Messages
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);

  // Fetch users and stocks on mount
  useEffect(() => {
    fetchUsers();
    fetchStocks();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.get('/api/leaderboard');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStocks = async () => {
    try {
      const response = await adminApi.get('/api/stocks');
      setStocks(response.data);
    } catch (error) {
      console.error('Error fetching stocks:', error);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // ========== AWARD / DEDUCT BALANCE ==========
  const fetchGameTeams = async (game) => {
    if (!game) { setGameTeams([]); return; }
    try {
      const response = await adminApi.get(`/api/admin/teams/${game}`);
      if (response.data.success) setGameTeams(response.data.teams);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const awardFilteredUsers = users.filter(user =>
    awardUserSearch &&
    (user.username?.toLowerCase().includes(awardUserSearch.toLowerCase()) ||
     user.shortId?.toLowerCase().includes(awardUserSearch.toLowerCase()))
  );

  const toggleAwardUser = (user) => {
    setAwardSelectedUsers(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) return prev.filter(u => u.id !== user.id);
      return [...prev, user];
    });
  };

  const handleAward = async () => {
    const amount = parseFloat(awardAmount);
    if (isNaN(amount) || amount === 0) {
      showMessage('Enter a valid amount (positive to award, negative to deduct)', 'error');
      return;
    }

    let userIds = [];
    let targetLabel = '';

    if (awardMode === 'user') {
      if (awardSelectedUsers.length === 0) {
        showMessage('Select at least one user', 'error');
        return;
      }
      userIds = awardSelectedUsers.map(u => u.id);
      targetLabel = awardSelectedUsers.map(u => u.username).join(', ');
    } else {
      if (!awardGame || awardTeam === '') {
        showMessage('Select a game and team', 'error');
        return;
      }
      const team = gameTeams.find(t => t.team === parseInt(awardTeam));
      if (!team || team.members.length === 0) {
        showMessage('Team has no members', 'error');
        return;
      }
      userIds = team.members.map(m => m.userId);
      targetLabel = `${team.name} (${team.members.length} members)`;
    }

    const action = amount > 0 ? 'Award' : 'Deduct';
    const absAmount = Math.abs(amount);
    if (!window.confirm(`${action} $${absAmount.toFixed(2)} ${amount > 0 ? 'to' : 'from'} ${targetLabel}?\n\nReason: ${awardReason || 'Admin adjustment'}\nAffects ${userIds.length} user(s)`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await adminApi.post('/api/admin/bulk-balance-adjustment', {
        amount,
        reason: awardReason || 'Admin adjustment',
        userIds
      });
      const count = response.data.affectedUsers?.length || userIds.length;
      showMessage(`${action}ed $${absAmount.toFixed(2)} ${amount > 0 ? 'to' : 'from'} ${count} user(s)`, 'success');
      setAwardAmount('');
      setAwardReason('');
      setAwardSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to adjust balance', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== USER BALANCE OVERRIDE ==========
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.shortId?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSetUserBalance = async () => {
    if (!selectedUser || !newBalance) {
      showMessage('Please select a user and enter a new balance', 'error');
      return;
    }

    const balanceNum = parseFloat(newBalance);
    if (isNaN(balanceNum) || balanceNum < 0) {
      showMessage('Please enter a valid positive number', 'error');
      return;
    }

    if (!window.confirm(`Set ${selectedUser.username}'s balance to $${balanceNum.toFixed(2)}?\n\nCurrent: $${selectedUser.totalValue.toFixed(2)}\nNew: $${balanceNum.toFixed(2)}`)) {
      return;
    }

    setLoading(true);
    try {
      await adminApi.post('/api/admin/user/set-balance', {
        userId: selectedUser.id,
        newBalance: balanceNum
      });

      showMessage(`✅ Set ${selectedUser.username}'s balance to $${balanceNum.toFixed(2)}`, 'success');
      setNewBalance('');
      setSelectedUser(null);
      setUserSearch('');
      fetchUsers(); // Refresh
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to set balance', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== STOCK PRICE OVERRIDE ==========
  const handleSetStockPrice = async () => {
    if (!selectedStock || !newStockPrice) {
      showMessage('Please select a stock and enter a new price', 'error');
      return;
    }

    const priceNum = parseFloat(newStockPrice);
    if (isNaN(priceNum) || priceNum <= 0) {
      showMessage('Please enter a valid positive price', 'error');
      return;
    }

    const stock = stocks.find(s => s.id === selectedStock);
    if (!stock) return;

    if (!window.confirm(`Set ${stock.ticker} price to $${priceNum.toFixed(2)}?\n\nCurrent: $${stock.currentPrice.toFixed(2)}\nNew: $${priceNum.toFixed(2)}`)) {
      return;
    }

    setLoading(true);
    try {
      await adminApi.post('/api/admin/stock/set-price', {
        stockId: selectedStock,
        newPrice: priceNum
      });

      showMessage(`✅ Set ${stock.ticker} price to $${priceNum.toFixed(2)}`, 'success');
      setNewStockPrice('');
      setSelectedStock('');
      fetchStocks(); // Refresh
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to set stock price', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ========== RESET INDIVIDUAL USER ==========
  const handleResetUser = async () => {
    if (!resetUserId) {
      showMessage('Please select a user to reset', 'error');
      return;
    }

    const user = users.find(u => u.id === resetUserId);
    if (!user) return;

    if (!window.confirm(`⚠️ RESET ${user.username}?\n\nThis will:\n- Set balance to $10,000\n- Close all positions\n- Clear transaction history\n\nThis CANNOT be undone!`)) {
      return;
    }

    setLoading(true);
    try {
      await adminApi.post('/api/admin/user/reset', { userId: resetUserId });

      showMessage(`✅ Reset ${user.username} successfully`, 'success');
      setResetUserId('');
      fetchUsers(); // Refresh
    } catch (error) {
      showMessage(error.response?.data?.message || 'Failed to reset user', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-overrides">
      <div className="overrides-header">
        <h2>🔧 Admin Overrides</h2>
        <p className="warning-note">⚠️ Use these tools carefully - changes are immediate and irreversible</p>
      </div>

      {message.text && (
        <div className={`override-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* AWARD / DEDUCT BALANCE */}
      <div className="override-section">
        <h3>🏆 Award / Deduct Balance</h3>
        <p className="section-desc">Quickly add or remove balance from users or entire teams</p>

        <div className="override-form">
          <div className="form-group" style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button
              className={`quick-action-btn ${awardMode === 'user' ? 'active' : ''}`}
              onClick={() => setAwardMode('user')}
              style={awardMode === 'user' ? { background: '#00aa44', color: '#fff' } : {}}
            >
              By User
            </button>
            <button
              className={`quick-action-btn ${awardMode === 'team' ? 'active' : ''}`}
              onClick={() => setAwardMode('team')}
              style={awardMode === 'team' ? { background: '#00aa44', color: '#fff' } : {}}
            >
              By Team
            </button>
          </div>

          {awardMode === 'user' && (
            <>
              <div className="form-group">
                <label>Select Users:</label>
                <select
                  onChange={(e) => {
                    const user = users.find(u => u.id === e.target.value);
                    if (user) toggleAwardUser(user);
                    e.target.value = '';
                  }}
                  className="stock-select"
                >
                  <option value="">Choose a user...</option>
                  {users
                    .filter(u => !awardSelectedUsers.find(s => s.id === u.id))
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username || user.displayName} (#{user.shortId}) — ${user.balance?.toFixed(2) || '0.00'}
                      </option>
                    ))}
                </select>
              </div>

              {awardSelectedUsers.length > 0 && (
                <div className="selected-user-info" style={{ marginTop: '8px' }}>
                  <strong>Selected ({awardSelectedUsers.length}):</strong>{' '}
                  {awardSelectedUsers.map((u, i) => (
                    <span key={u.id} style={{ cursor: 'pointer', marginRight: '6px' }} onClick={() => toggleAwardUser(u)}>
                      {u.username} ✕{i < awardSelectedUsers.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}

          {awardMode === 'team' && (
            <>
              <div className="form-group">
                <label>Game:</label>
                <select
                  value={awardGame}
                  onChange={(e) => { setAwardGame(e.target.value); setAwardTeam(''); fetchGameTeams(e.target.value); }}
                  className="stock-select"
                >
                  <option value="">Choose game...</option>
                  <option value="battleship">Battleship (4 teams)</option>
                  <option value="briefcase">Briefcase (8 teams)</option>
                  <option value="golf">Dead Cat Bounce (8 teams)</option>
                </select>
              </div>

              {gameTeams.length > 0 && (
                <div className="form-group">
                  <label>Team:</label>
                  <select
                    value={awardTeam}
                    onChange={(e) => setAwardTeam(e.target.value)}
                    className="stock-select"
                  >
                    <option value="">Choose team...</option>
                    {gameTeams.map(t => (
                      <option key={t.team} value={t.team}>
                        ({t.team + 1}) {t.name} — {t.members.length} members
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {awardTeam !== '' && gameTeams.length > 0 && (
                <div className="selected-user-info" style={{ marginTop: '8px' }}>
                  {(() => {
                    const team = gameTeams.find(t => t.team === parseInt(awardTeam));
                    if (!team) return null;
                    return <>
                      <strong>{team.name} ({team.members.length} members):</strong>{' '}
                      {team.members.map(m => m.username).join(', ') || 'No members'}
                    </>;
                  })()}
                </div>
              )}
            </>
          )}

          <div className="form-group" style={{ marginTop: '12px' }}>
            <label>Amount (positive = award, negative = deduct):</label>
            <input
              type="number"
              value={awardAmount}
              onChange={(e) => setAwardAmount(e.target.value)}
              placeholder="e.g. 500 or -200"
              step="0.01"
              className="number-input"
            />
          </div>

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {[100, 500, 1000, 2500, 5000, -500, -1000].map(v => (
              <button key={v} className="quick-action-btn" onClick={() => setAwardAmount(String(v))} style={{ fontSize: '0.85em', padding: '4px 10px' }}>
                {v > 0 ? '+' : ''}${v.toLocaleString()}
              </button>
            ))}
          </div>

          <div className="form-group">
            <label>Reason (optional):</label>
            <input
              type="text"
              value={awardReason}
              onChange={(e) => setAwardReason(e.target.value)}
              placeholder="e.g. Jeopardy winner bonus"
              className="search-input"
            />
          </div>

          <button
            onClick={handleAward}
            disabled={loading || !awardAmount}
            className="override-button primary"
            style={{ marginTop: '8px' }}
          >
            {loading ? 'Processing...' : parseFloat(awardAmount) >= 0 ? '🏆 Award Balance' : '💸 Deduct Balance'}
          </button>
        </div>
      </div>

      {/* USER BALANCE OVERRIDE */}
      <div className="override-section">
        <h3>💰 Set User Balance</h3>
        <p className="section-desc">Directly set a user's cash balance (does not affect positions)</p>

        <div className="override-form">
          <div className="form-group">
            <label>Select User:</label>
            <select
              value={selectedUser?.id || ''}
              onChange={(e) => {
                const user = users.find(u => u.id === e.target.value);
                setSelectedUser(user || null);
              }}
              className="stock-select"
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username || user.displayName} (#{user.shortId}) — ${user.balance?.toFixed(2) || '0.00'}
                </option>
              ))}
            </select>
          </div>

          {selectedUser && (
            <>
              <div className="selected-user-info">
                <strong>Selected:</strong> {selectedUser.username}
                <br />
                <strong>Current Cash Balance:</strong> ${selectedUser.balance?.toFixed(2)}
                <br />
                <strong>Total Value (with positions):</strong> ${selectedUser.totalValue?.toFixed(2)}
              </div>

              <div className="form-group">
                <label>New Balance:</label>
                <input
                  type="number"
                  value={newBalance}
                  onChange={(e) => setNewBalance(e.target.value)}
                  placeholder="Enter new balance..."
                  min="0"
                  step="0.01"
                  className="number-input"
                />
              </div>

              <button
                onClick={handleSetUserBalance}
                disabled={loading || !newBalance}
                className="override-button primary"
              >
                {loading ? '⏳ Setting...' : '✅ Set Balance'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* STOCK PRICE OVERRIDE */}
      <div className="override-section">
        <h3>📈 Set Stock Price</h3>
        <p className="section-desc">Manually set a stock's current price (affects all positions)</p>

        <div className="override-form">
          <div className="form-group">
            <label>Select Stock:</label>
            <select
              value={selectedStock}
              onChange={(e) => setSelectedStock(e.target.value)}
              className="stock-select"
            >
              <option value="">Choose a stock...</option>
              {stocks.map(stock => (
                <option key={stock.id} value={stock.id}>
                  {stock.ticker} - {stock.name} (Current: ${stock.currentPrice?.toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          {selectedStock && (
            <>
              <div className="selected-stock-info">
                {(() => {
                  const stock = stocks.find(s => s.id === selectedStock);
                  return stock ? (
                    <>
                      <strong>Selected:</strong> {stock.ticker} - {stock.name}
                      <br />
                      <strong>Current Price:</strong> ${stock.currentPrice?.toFixed(2)}
                      <br />
                      <strong>Base Price:</strong> ${stock.basePrice?.toFixed(2)}
                    </>
                  ) : null;
                })()}
              </div>

              <div className="form-group">
                <label>New Price:</label>
                <input
                  type="number"
                  value={newStockPrice}
                  onChange={(e) => setNewStockPrice(e.target.value)}
                  placeholder="Enter new price..."
                  min="0.01"
                  step="0.01"
                  className="number-input"
                />
              </div>

              <button
                onClick={handleSetStockPrice}
                disabled={loading || !newStockPrice}
                className="override-button primary"
              >
                {loading ? '⏳ Setting...' : '✅ Set Price'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* RESET INDIVIDUAL USER */}
      <div className="override-section danger-section">
        <h3>🔄 Reset Individual User</h3>
        <p className="section-desc">Reset a single user to starting state ($10,000, no positions)</p>

        <div className="override-form">
          <div className="form-group">
            <label>Select User to Reset:</label>
            <select
              value={resetUserId}
              onChange={(e) => setResetUserId(e.target.value)}
              className="user-select"
            >
              <option value="">Choose a user...</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username || user.displayName} (Balance: ${user.balance?.toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          {resetUserId && (
            <button
              onClick={handleResetUser}
              disabled={loading}
              className="override-button danger"
            >
              {loading ? '⏳ Resetting...' : '⚠️ Reset User'}
            </button>
          )}
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div className="override-section">
        <h3>⚡ Quick Actions</h3>
        <div className="quick-actions">
          <button
            onClick={() => {
              setNewBalance('10000');
              showMessage('Set to starting balance ($10,000)', 'info');
            }}
            className="quick-action-btn"
          >
            💵 Use Starting Balance
          </button>
          <button
            onClick={() => {
              setNewBalance('50000');
              showMessage('Set to $50,000', 'info');
            }}
            className="quick-action-btn"
          >
            💰 Set $50,000
          </button>
          <button
            onClick={() => {
              setNewBalance('100000');
              showMessage('Set to $100,000', 'info');
            }}
            className="quick-action-btn"
          >
            💎 Set $100,000
          </button>
          <button
            onClick={() => {
              fetchUsers();
              fetchStocks();
              showMessage('Data refreshed', 'info');
            }}
            className="quick-action-btn"
          >
            🔄 Refresh Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminOverrides;
